
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";

const DURATION = 5000;

interface UndoToastProps {
  toastId: string | number;
  message: string;
  onUndo: () => void;
}

export function UndoToast({ toastId, message, onUndo }: UndoToastProps) {
  const [progress, setProgress] = useState(100);
  const startRef = useRef(Date.now());
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const remaining = Math.max(0, 1 - elapsed / DURATION);
      setProgress(remaining * 100);
      if (remaining > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleUndo = () => {
    cancelAnimationFrame(rafRef.current);
    onUndo();
    toast.dismiss(toastId);
  };

  return (
    <div className="w-full flex flex-col gap-2 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">{message}</span>
        <button
          onClick={handleUndo}
          className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
        >
          <Undo2 className="h-3 w-3" />
          Undo
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-0.5 w-full bg-border/60 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary/60 rounded-full transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/** Fire a delete + show the undo toast. Returns the toast id. */
export function toastUndo(
  message: string,
  onUndo: () => void,
): string | number {
  return toast.custom(
    (id) => <UndoToast toastId={id} message={message} onUndo={onUndo} />,
    { duration: DURATION },
  );
}
